import React from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import './FAQ.css';

const FAQ = () => {
  const faqs = [
    {
      question: "How do I buy a painting from Canvaart?",
      answer: "Browse our Art Gallery to find a painting you love. Click 'Contact to Buy' on any artwork, fill in your details, and we will get back to you within 24 hours with payment and delivery options. You can also request a video call to see the painting in detail before purchasing."
    },
    {
      question: "Are these original paintings or prints?",
      answer: "Every single piece on Canvaart is a 100% hand-painted original artwork. No prints, no reproductions. Each painting comes with a signed Certificate of Authenticity guaranteeing its originality."
    },
    {
      question: "How long does a custom painting commission take?",
      answer: "Most custom commissions take 2–4 weeks depending on complexity, size, and medium. Oil paintings may take slightly longer (3–5 weeks) due to drying time. We provide regular photo updates throughout the creation process so you can watch your vision come to life."
    },
    {
      question: "What sizes are available for custom commissions?",
      answer: "We offer a wide range of canvas sizes from small 12x16 inch pieces to large statement 48x60 inch artworks. During your custom request consultation, we will recommend the best proportion based on your wall space, room size, and budget."
    },
    {
      question: "Do you ship paintings across India?",
      answer: "Yes! We ship securely across all of India. Every painting is carefully wrapped in bubble wrap, placed in a sturdy corrugated box, and often shipped in a wooden crate for larger pieces. Shipping is free for orders above ₹5,000."
    },
    {
      question: "What is your return or exchange policy?",
      answer: "Due to the unique nature of original artwork, returns are accepted only if the painting arrives damaged. Please photograph the package before opening and contact us within 48 hours of delivery. We will arrange a replacement or full refund."
    },
    {
      question: "How do art auctions work on Canvaart?",
      answer: "Visit the Auctions page to see currently active auctions. Enter your bid amount and click 'Place Bid'. The highest bid at the auction end time wins. You will receive email notifications if you are outbid. All auction winners receive the original painting with a Certificate of Authenticity."
    },
    {
      question: "Can I see the painting in person before buying?",
      answer: "For buyers in the same city, studio visits can be arranged by appointment. For remote buyers, we offer high-resolution photos, 360-degree views, and video calls to show the painting's texture, colors, and details before purchase."
    },
    {
      question: "What payment methods do you accept?",
      answer: "We accept UPI (Google Pay, PhonePe, Paytm), bank transfer, credit/debit cards via Razorpay, and Cash on Delivery for select locations. For custom commissions, we typically require a 50% advance to begin work."
    },
    {
      question: "Do you offer framing services?",
      answer: "Yes! We can arrange professional framing in styles that complement your painting — from minimalist floater frames to ornate classic frames. Frame options and pricing are discussed during your purchase or custom request consultation."
    },
    {
      question: "How should I care for my canvas painting?",
      answer: "Keep your painting away from direct sunlight and moisture. Dust gently with a soft, dry cloth. Avoid using water or cleaning chemicals. All paintings are varnished for protection, but occasional re-varnishing after 5–7 years helps preserve vibrancy."
    },
    {
      question: "Can I request a painting similar to one that is already sold?",
      answer: "Absolutely! If a painting you love has been sold, you can request a similar custom piece. While no two original paintings are identical, I can create a new work in the same style, color palette, and mood that captures what you loved about the original."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <div className="faq-page">
      <SEO
        title="Frequently Asked Questions"
        description="Find answers about buying original paintings, custom artwork commissions, shipping across India, auction rules, and caring for your canvas art."
        keywords="buy paintings FAQ, custom art questions, canvas painting care, art auction rules, painting shipping India, original artwork FAQ"
        url="/faq"
        schema={faqSchema}
      />

      <header className="faq-header">
        <div className="container">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Frequently Asked Questions
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Everything you need to know about buying art and custom commissions
          </motion.p>
        </div>
      </header>

      <section className="faq-content container" aria-label="Frequently asked questions">
        <div className="faq-list">
          {faqs.map((faq, index) => (
            <motion.details
              key={index}
              className="faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              viewport={{ once: true }}
            >
              <summary className="faq-question">
                <span>{faq.question}</span>
                <span className="faq-icon" aria-hidden="true">+</span>
              </summary>
              <div className="faq-answer">
                <p>{faq.answer}</p>
              </div>
            </motion.details>
          ))}
        </div>

        <motion.div
          className="faq-contact-cta"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2>Still have questions?</h2>
          <p>We're here to help. Reach out and we'll get back to you within 24 hours.</p>
          <div className="faq-contact-options">
            <a href="mailto:help@canvaart.com" className="btn-primary">Email Us</a>
            <a href="tel:+919933558299" className="btn-secondary">Call +91 9933558299</a>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default FAQ;
